import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Clock,
  Shield,
  Mail,
  Phone,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { ADMIN_CONFIG } from "../../utils/constants";

const PendingApprovalPage: React.FC = () => {
  const contactInfo = ADMIN_CONFIG.USER_MANAGEMENT.CONTACT_INFO;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Main Card */}
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              <div className="relative">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="h-8 w-8 text-orange-600" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-orange-800">
              Account Pending Approval
            </CardTitle>
            <p className="text-orange-700 mt-2">
              Your AltZero account is awaiting administrator approval
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            <Alert className="border-orange-200 bg-orange-50">
              <Shield className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                <strong>Welcome to AltZero!</strong> Your account has been
                created successfully, but requires approval from our
                administrators before you can access the platform.
              </AlertDescription>
            </Alert>

            {/* Status Steps */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Account Status:</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm">Account created successfully</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                    <Clock className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-medium text-orange-700">
                    Pending administrator approval
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                  </div>
                  <span className="text-sm text-gray-500">
                    Full platform access
                  </span>
                </div>
              </div>
            </div>

            {/* What happens next */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">
                What happens next?
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Our administrators will review your account</li>
                <li>• You'll receive an email notification once approved</li>
                <li>• After approval, you'll have full access to AltZero</li>
                <li>• This process typically takes 1-2 business days</li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="border-t pt-6">
              <h4 className="font-semibold text-gray-900 mb-4">
                Need help or have questions?
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-gray-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Mail className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Email Support
                        </p>
                        <a
                          href={`mailto:${contactInfo.EMAIL}`}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {contactInfo.EMAIL}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Phone className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Phone Support
                        </p>
                        <a
                          href={`tel:${contactInfo.PHONE}`}
                          className="text-sm text-green-600 hover:text-green-800"
                        >
                          {contactInfo.PHONE}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="flex-1"
              >
                Refresh Status
              </Button>
              <Button
                onClick={() =>
                  (window.location.href = `mailto:${contactInfo.EMAIL}?subject=Account Approval Request&body=Hello, I am requesting approval for my AltZero account. Thank you.`)
                }
                className="flex-1"
              >
                Contact Admin
              </Button>
            </div>

            {/* Additional Info */}
            <div className="text-center pt-4 border-t">
              <p className="text-xs text-gray-500">
                This is an automated security measure to ensure platform quality
                and security.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PendingApprovalPage;
 